import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { adminGetFiles, adminUploadFile, adminDeleteFile } from '../api';

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (date) =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const initialForm = {
  title: '',
  description: '',
  version: '',
  downloadUsername: '',
  downloadPassword: '',
};

export default function AdminDashboard() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(initialForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const res = await adminGetFiles();
      setFiles(res.data.files);
    } catch {
      setError('Failed to load files.');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0] || null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) setSelectedFile(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    data.append('file', selectedFile);

    try {
      setUploading(true);
      setUploadProgress(0);
      await adminUploadFile(data);
      setSuccess('File uploaded successfully!');
      setForm(initialForm);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadProgress(100);
      fetchFiles();
      setTimeout(() => {
        setSuccess('');
        setUploadProgress(0);
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.errors?.[0]?.msg ||
        'Upload failed.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminDeleteFile(id);
      setFiles((prev) => prev.filter((f) => f._id !== id));
      setDeleteConfirm(null);
      setSuccess('File deleted.');
      setTimeout(() => setSuccess(''), 2000);
    } catch {
      setError('Failed to delete file.');
    }
  };

  const totalSize = files.reduce((acc, f) => acc + (f.fileSize || 0), 0);
  const totalDownloads = files.reduce((acc, f) => acc + (f.downloadCount || 0), 0);

  const stats = [
    {
      label: 'Total Files',
      value: files.length,
      icon: (
        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
        </svg>
      ),
      bg: 'bg-indigo-50',
      border: 'border-indigo-100',
    },
    {
      label: 'Total Size',
      value: formatBytes(totalSize),
      icon: (
        <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
      ),
      bg: 'bg-violet-50',
      border: 'border-violet-100',
    },
    {
      label: 'Total Downloads',
      value: totalDownloads,
      icon: (
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
        </svg>
      ),
      bg: 'bg-blue-50',
      border: 'border-blue-100',
    },
    {
      label: 'Status',
      value: 'Active',
      icon: (
        <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
    },
  ];

  /* Shared input class */
  const inputCls =
    'w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 focus:bg-white transition-all duration-200';

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Navbar />

      {/* Subtle top background accent */}
      <div className="absolute top-16 left-0 right-0 h-64 bg-gradient-to-b from-indigo-50/60 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">

        {/* Page Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-mono text-gray-400 uppercase tracking-widest">
                Admin Panel
              </span>
            </div>
            <h1 className="text-3xl font-display font-bold text-gray-900 tracking-tight">
              Admin Dashboard
            </h1>
            <p className="text-gray-400 text-sm font-mono mt-1">
              Manage file uploads and access credentials
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-4 py-2.5 shadow-sm">
            <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm text-gray-500 font-mono">
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-200"
            >
              <div className={`w-10 h-10 ${stat.bg} border ${stat.border} rounded-xl flex items-center justify-center mb-3`}>
                {stat.icon}
              </div>
              <div className="text-2xl font-display font-bold text-gray-900">{stat.value}</div>
              <div className="text-gray-400 text-xs font-mono mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Alerts */}
        {error && (
          <div className="flex items-center justify-between bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-6 animate-fade-in">
            <div className="flex items-center gap-2.5">
              <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-600 text-sm font-mono">{error}</span>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-300 hover:text-red-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-6 animate-fade-in">
            <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-emerald-600 text-sm font-mono">{success}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100/80 border border-gray-200/60 rounded-xl p-1 mb-6 w-fit">
          {['upload', 'files'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === tab
                ? 'bg-white text-indigo-600 shadow-sm border border-gray-100'
                : 'text-gray-400 hover:text-gray-700'
                }`}
            >
              {tab === 'upload' ? (
                <span className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload File
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                  </svg>
                  Files
                  <span className="bg-indigo-100 text-indigo-600 text-xs px-1.5 py-0.5 rounded-full font-mono">
                    {files.length}
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Upload Tab ── */}
        {activeTab === 'upload' && (
          <div className="max-w-6xl mx-auto animate-fade-in">
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-7">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-gray-900 font-display font-semibold text-base">Upload New File</h2>
                  <p className="text-gray-400 text-xs font-mono">Fill in details and attach a file</p>
                </div>
              </div>

              <form onSubmit={handleUpload} className="space-y-5">

                {/* Drop Zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${isDragging
                    ? 'border-indigo-400 bg-indigo-50/80 scale-[1.01]'
                    : selectedFile
                      ? 'border-indigo-300 bg-indigo-50/40'
                      : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30'
                    }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".zip,.exe,.msi,.rar,.7z,.tar,.gz"
                  />
                  {selectedFile ? (
                    <div>
                      <div className="w-12 h-12 bg-indigo-100 border border-indigo-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-gray-800 font-mono text-sm font-medium">{selectedFile.name}</p>
                      <p className="text-gray-400 text-xs mt-1 font-mono">{formatBytes(selectedFile.size)}</p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="mt-3 text-xs text-red-400 hover:text-red-600 font-mono border border-red-100 hover:border-red-200 hover:bg-red-50 px-3 py-1 rounded-lg transition-all duration-200"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="w-12 h-12 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                      </div>
                      <p className="text-gray-600 text-sm font-medium">
                        Drop file here or{' '}
                        <span className="text-indigo-600">browse</span>
                      </p>
                      <p className="text-gray-300 text-xs mt-1 font-mono">
                        .zip .exe .msi .rar .7z — max 500MB
                      </p>
                    </div>
                  )}
                </div>

                {/* Form fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-gray-500 text-xs font-mono uppercase tracking-widest mb-2">
                      Title <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={form.title}
                      onChange={handleFormChange}
                      className={inputCls}
                      placeholder="My Application v1.0"
                      required
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-gray-500 text-xs font-mono uppercase tracking-widest mb-2">
                      Description <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleFormChange}
                      className={`${inputCls} resize-none h-24`}
                      placeholder="Brief description of this file..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-500 text-xs font-mono uppercase tracking-widest mb-2">
                      Version <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="version"
                      value={form.version}
                      onChange={handleFormChange}
                      className={inputCls}
                      placeholder="1.0.0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-500 text-xs font-mono uppercase tracking-widest mb-2">
                      Access Username <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="downloadUsername"
                      value={form.downloadUsername}
                      onChange={handleFormChange}
                      className={inputCls}
                      placeholder="user123"
                      required
                      autoComplete="off"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-gray-500 text-xs font-mono uppercase tracking-widest mb-2">
                      Access Password <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="password"
                      name="downloadPassword"
                      value={form.downloadPassword}
                      onChange={handleFormChange}
                      className={inputCls}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                    <p className="text-gray-300 text-xs font-mono mt-1.5 flex items-center gap-1.5">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Password is hashed with bcrypt before storage.
                    </p>
                  </div>
                </div>

                {/* Progress bar */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div>
                    <div className="flex justify-between text-xs font-mono text-gray-400 mb-1.5">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl shadow-sm shadow-indigo-200 transition-all duration-200"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Upload File
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ── Files Tab ── */}
        {activeTab === 'files' && (
          <div className="animate-fade-in">
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white border border-gray-100 rounded-2xl p-5 animate-pulse flex items-center gap-4 shadow-sm"
                  >
                    <div className="w-11 h-11 bg-gray-100 rounded-xl flex-shrink-0" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-100 rounded-lg w-1/3 mb-2.5" />
                      <div className="h-3 bg-gray-100 rounded-lg w-2/3" />
                    </div>
                    <div className="w-24 h-8 bg-gray-100 rounded-lg" />
                  </div>
                ))}
              </div>
            ) : files.length === 0 ? (
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-16 text-center">
                <div className="w-14 h-14 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-4">No files uploaded yet.</p>
                <button
                  onClick={() => setActiveTab('upload')}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl shadow-sm shadow-indigo-200 transition-all duration-200"
                >
                  Upload First File
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {files.map((file) => (
                  <div
                    key={file._id}
                    className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-gray-200 flex flex-col sm:flex-row sm:items-center gap-4 transition-all duration-200"
                  >
                    {/* Icon */}
                    <div className="w-11 h-11 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-900 font-display font-semibold">{file.title}</span>
                        <span className="text-xs font-mono text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
                          v{file.version}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm mt-0.5 truncate">{file.description}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-xs font-mono text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-lg">
                          {file.originalName}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-mono text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-lg">
                          {formatBytes(file.fileSize)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-mono text-blue-500 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-lg">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                          </svg>
                          {file.downloadCount || 0} downloads
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-mono text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-lg">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {file.downloadUsername}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-gray-300 text-xs font-mono hidden lg:block">
                        {formatDate(file.createdAt)}
                      </span>

                      {deleteConfirm === file._id ? (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-1.5">
                          <span className="text-xs text-red-500 font-mono">Delete?</span>
                          <button
                            onClick={() => handleDelete(file._id)}
                            className="text-xs text-white bg-red-500 hover:bg-red-600 font-mono px-2.5 py-1 rounded-lg transition-all duration-200"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 font-mono px-2 py-1 rounded-lg hover:bg-gray-100 transition-all duration-200"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(file._id)}
                          className="text-gray-300 hover:text-red-500 transition-colors p-2 rounded-xl hover:bg-red-50 border border-transparent hover:border-red-100"
                          title="Delete file"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}